import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Live clock (updates every second) for the header date/time box.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const _p = (n) => String(n).padStart(2, "0");
  const _MON = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEPT", "OCT", "NOV", "DEC"];
  const clockTime = `${_p(now.getHours())}:${_p(now.getMinutes())}:${_p(now.getSeconds())}`;
  const clockDate = `${_p(now.getDate())} ${_MON[now.getMonth()]} ${now.getFullYear()}`;

  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    setErr("");
    // Round 36 — surface silent failures (empty fields)
    if (!email || !password) {
      setErr("Please enter both email and password.");
      return;
    }
    setBusy(true);

    // Silent retry helper. Calls login() and classifies the failure:
    //  • network-level (no response) → backend cold-start / blip → RETRY
    //  • transient gateway errors (Cloudflare 520–527/530, 502/503/504) where the origin
    //    was briefly unavailable and returned no valid HTTP → RETRY
    //  • real structured 4xx/5xx (bad creds, 500 app error) → surface, do NOT retry
    const TRANSIENT = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530]);
    const isTransient = (err) => !err?.response || TRANSIENT.has(err.response.status);
    const attemptLogin = async () => {
      try {
        await login(email.trim(), password);
        return { ok: true };
      } catch (err) {
        return { ok: false, err };
      }
    };

    // Up to 3 attempts with a short backoff, but only while the error looks transient.
    let result = await attemptLogin();
    for (let attempt = 1; attempt <= 2 && !result.ok && isTransient(result.err); attempt++) {
      await new Promise((r) => setTimeout(r, 1500 * attempt));
      result = await attemptLogin();
    }

    if (result.ok) {
      nav(from, { replace: true });
    } else {
      const status = result.err?.response?.status;
      const msg = (!result.err?.response || (status && TRANSIENT.has(status)))
        ? "Server is waking up / briefly unavailable. Please try again in a moment."
        : (result.err?.response?.data?.detail || "Login failed. Please try again.");
      setErr(msg);
    }
    setBusy(false);
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-slate-950 text-white"
      style={{
        // Parts-showcase photo as the hero background (drop the file at frontend/public/parts-wall.jpg).
        // The layered gradient darkens the left for legibility and keeps a graceful look before the
        // image is present, so the page never appears broken.
        backgroundImage:
          // Lighter overlay so the parts photo stays clearly visible; a soft dark band on the
          // left keeps the headline readable, and a gentle bottom vignette anchors the page.
          "linear-gradient(105deg, rgba(8,12,22,0.80) 0%, rgba(8,12,22,0.48) 34%, rgba(8,12,22,0.16) 64%, rgba(8,12,22,0.06) 100%), " +
          "linear-gradient(0deg, rgba(8,12,22,0.45) 0%, rgba(8,12,22,0.00) 30%), " +
          "radial-gradient(1100px 560px at 85% 12%, rgba(234,88,12,0.12), transparent 60%), " +
          "url(/parts-wall.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center right",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Blueprint dot-grid overlay — subtle engineering texture over the photo. */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.18]" aria-hidden="true">
        <defs>
          <pattern id="lgrid" width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#e2e8f0" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lgrid)" />
      </svg>

      {/* Top status bar — brand left, live "NPD" tag right (distinct amber identity). */}
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4">
        <div className="flex items-center gap-4 rounded-2xl border border-white/12 bg-slate-900/55 px-4 py-3 shadow-xl shadow-black/40 backdrop-blur-md">
          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl bg-white p-2 shadow-lg shadow-black/40 ring-2 ring-orange-400/50">
            <img src="/aampl-logo.png" alt="AAMPL" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-lg sm:text-xl font-bold tracking-wide">ADVANCED ANMOL METCOMP</div>
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-orange-400">New Product Development</div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 rounded-full border border-white/12 bg-slate-900/55 px-4 py-2 shadow-xl shadow-black/40 backdrop-blur-md">
          <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.6)]" />
          <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-200">NPD System · Online</span>
        </div>
      </header>

      {/* Body — showcase caption (left) + sign-in card (right). Aligned to the top so more of
          the parts photo stays visible below. */}
      <main className="relative z-10 mx-auto flex max-w-6xl flex-col items-stretch gap-8 px-5 pt-6 sm:px-8 lg:flex-row lg:items-start lg:pt-10">
        {/* Left — brand story / parts caption on the photo (no box), left side only. */}
        <section className="hidden flex-1 lg:block">
          <div className="max-w-lg">
            <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-orange-500/40 bg-orange-500/15 px-2.5 py-1 font-mono text-[11px] tracking-wide text-orange-300 backdrop-blur-sm">
              DWG → PART · COSTING · APQP
            </div>
            <h1 className="text-4xl font-black leading-[1.08] tracking-tight sm:text-5xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]">
              From drawing<br />to{" "}
              <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">precision part.</span>
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-200 drop-shadow-[0_1px_8px_rgba(0,0,0,0.9)]">
              The AAMPL New Product Development workspace — RFQ &amp; costing, assemblies, drawing
              ballooning, and APQP — for the sheet-metal &amp; machined components we engineer every day.
            </p>
            {/* small engineering "spec" chips echoing the parts wall */}
            <div className="mt-7 flex flex-wrap gap-2.5">
              {["Sheet-metal", "Machined", "Assemblies", "APQP", "Inspection"].map((t) => (
                <span key={t} className="rounded-full border border-white/15 bg-black/25 px-3 py-1 text-[11px] font-medium text-slate-100 backdrop-blur-sm">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Right — compact sign-in card with engineering crop-mark frame. */}
        <section className="flex w-full items-start justify-center lg:w-[380px]">
          <div className="relative w-full max-w-sm">
            {/* corner crop-marks (like an engineering drawing frame) */}
            <span className="pointer-events-none absolute -left-2 -top-2 h-5 w-5 border-l-2 border-t-2 border-orange-400/70" />
            <span className="pointer-events-none absolute -right-2 -top-2 h-5 w-5 border-r-2 border-t-2 border-orange-400/70" />
            <span className="pointer-events-none absolute -bottom-2 -left-2 h-5 w-5 border-b-2 border-l-2 border-orange-400/70" />
            <span className="pointer-events-none absolute -bottom-2 -right-2 h-5 w-5 border-b-2 border-r-2 border-orange-400/70" />

            <div className="overflow-hidden rounded-2xl border border-white/15 bg-slate-900/65 shadow-2xl shadow-black/60 ring-1 ring-white/5 backdrop-blur-2xl">
              {/* amber top accent strip */}
              <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500" />
              <div className="p-6">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold tracking-tight">Sign in</h2>
                  <p className="mt-0.5 text-xs text-slate-400">Use your AAMPL NPD credentials.</p>
                </div>
                <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">NPD-F-01</div>
              </div>

              <form onSubmit={onSubmit} method="post" action="#" noValidate className="space-y-3.5" data-testid="login-form">
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-3.5 text-slate-400" />
                    <input
                      type="email" required autoFocus autoComplete="email"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@aampl.in"
                      className="w-full rounded-lg border border-white/15 bg-white/95 py-2.5 pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 shadow-inner focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-3.5 text-slate-400" />
                    <input
                      type={showPw ? "text" : "password"} required autoComplete="current-password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Your password"
                      className="w-full rounded-lg border border-white/15 bg-white/95 py-2.5 pl-9 pr-10 text-sm text-slate-900 placeholder-slate-400 shadow-inner focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                      data-testid="login-password"
                    />
                    <button type="button" onClick={() => setShowPw((v) => !v)}
                            className="absolute right-2 top-2.5 p-0.5 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {err && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-400/30 bg-red-500/15 px-3 py-2 text-sm text-red-200" data-testid="login-error">
                    <AlertCircle size={16} /> {err}
                  </div>
                )}

                <button
                  type="submit" disabled={busy || authLoading}
                  className="w-full rounded-lg bg-gradient-to-r from-orange-600 to-amber-500 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-all hover:from-orange-500 hover:to-amber-400 disabled:opacity-60"
                  data-testid="login-submit"
                >
                  {busy ? "Signing in…" : authLoading ? "Preparing…" : "Sign in →"}
                </button>
              </form>

              <div className="mt-5 flex items-center justify-center border-t border-white/10 pt-3 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Secured connection
                </span>
              </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Live date/time (with seconds) — plain text, no box, sitting inside the empty red
          display case on the parts wall. */}
      <div className="pointer-events-none absolute bottom-[16%] left-[44%] z-10 hidden -translate-x-1/2 flex-col items-center text-center lg:flex">
        <div className="font-mono text-xl font-bold tabular-nums tracking-[0.12em] text-orange-300 drop-shadow-[0_2px_10px_rgba(0,0,0,0.95)]">
          {clockTime}
        </div>
        <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-amber-100 drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]">
          {clockDate}
        </div>
      </div>

      <div className="relative z-10 pb-6" />
    </div>
  );
}
